import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Row, Col, Button, Modal, Form, Select, notification } from 'tea-component';
import FollowerRange from '@sg/components/FollowerRange';
import AddNewContentModal from './components/AddNewContentModal';
import DashboardMetricCard from './components/DashboardMetricCard';
import CardCommon from '@sg/components/CardCommon';
import ContentCard from '@sg/components/ContentCard';
import PlusIcon from '@sg/assests/svg/plus-icon.svg';
import GameDefault from '@/pages/home/assets/img/common/game-default.svg';
import './index.scss';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import { useIntl, getLocale } from 'umi';
import { LANGUAGE_EN } from '@home/_config/config';
import { getGameList, getProjectHighlights, batchUpdateProjectHighlight, getDashboardCardMetrics, getWeeklyCardMetrics, getMonthlyCardMetrics, getDashboardRainbowInfo } from '@/pages/strategic/services';
import DragHandle from '@sg/assests/svg/draghandle-icon.svg';
import Loading from '@intelligence/components/Loading';
import { SGBuriedBtnPoint, createBtnPoint, ExecutiveSummaryTab } from '@sg/utils/buriedPoint';
import AutoSaveModal from '@/pages/strategic/components/AutoSaveModal';
import RoutePrompt from '@sg/components/RoutePrompt';
import { rendTitle } from '@sg/utils/commonPageMethod';
import { processRainbowInfo, getDateRangeByGranularity, findMetricInfo, type MetricTextInfo } from './utils/metricUtils';

// @ts-ignore
import * as rbd from 'react-beautiful-dnd';
import { ReactSortable } from 'react-sortablejs';

import {
  DashboardContentData,
  AISummaryContentData,
  TextContentData,
  ContentData,
  ProjectCard,
  Project
} from './types';
import {
  getDashboardOverviewPath,
  METRIC_NO_ACCESS_CODE,
  METRIC_NO_ACCESS_STATUS,
} from './config';
import useProjectAutoScroll from './hooks/useProjectAutoScroll';
import { useWeeklyProjectHighlightAutoSave } from './hooks/useWeeklyProjectHighlightAutoSave';

interface WeeklyProjectHighlightProps {
  editable?: boolean;
  userPermissions?: {
    canEdit?: boolean;
  };
}

const WeeklyProjectHighlight: React.FC<WeeklyProjectHighlightProps> = ({
  editable = false,
  userPermissions = { canEdit: false }
}) => {
  const { formatMessage, locale } = useIntl();
  const isEn = getLocale() === LANGUAGE_EN;
  const [isEditing, setIsEditing] = useState(false);
  const [isAddProjectModalVisible, setIsAddProjectModalVisible] = useState(false);
  const [isAddContentModalVisible, setIsAddContentModalVisible] = useState(false);
  const [selectedProject, setSelectedProject] = useState('');
  const [contentModalMode, setContentModalMode] = useState<'new' | 'existing'>('new');
  const [existingProjectData, setExistingProjectData] = useState<{
    id: number;
    game_code: string;
    project_name: string;
    order: number;
    content_orders: string;
  } | undefined>(undefined);
  const [editingContent, setEditingContent] = useState<{
    id: string;
    type: string;
    data: any;
  } | null>(null);
  const [projectOptions, setProjectOptions] = useState<any[]>([]); // this is for the project dropdown from dashboard
  // Projects data structure for drag and drop
  const [projects, setProjects] = useState<Project[]>([]); // this is all the weekly highlight projects data that is fetched from the API
  const [originalProjects, setOriginalProjects] = useState<Project[]>([]); // Store original data for comparison
  const [loading, setLoading] = useState(false);
  // Store metric text info per game code: { [gameCode]: Array<MetricTextInfo> }
  const [metricTextInfoMap, setMetricTextInfoMap] = useState<Record<string, MetricTextInfo[]>>({});
  // Store batched metrics data per game code: { [gameCode]: { card_list: [...], trend_list: [...] } }
  const [batchedMetricsDataMap, setBatchedMetricsDataMap] = useState<Record<string, { card_list: any[], trend_list: any[] }>>({});
  const [metricsLoading, setMetricsLoading] = useState<Record<string, boolean>>({});

  // State for tracking pending deletions (similar to MKTCost)
  const [pendingProjectDeletes, setPendingProjectDeletes] = useState<Project[]>([]);
  const [pendingContentDeletes, setPendingContentDeletes] = useState<Array<{ id: string, projectId: string }>>([]);

  // Counter ref for generating temporary IDs
  const tempIdCounterRef = useRef({ project: 0, content: 0 });

  // Auto-scroll to project when returning from preview
  useProjectAutoScroll(projects, loading);

  const history = useHistory();
  const canEdit = editable && userPermissions.canEdit;

  const isMetricNoAccessError = (error: any): boolean => {
    const responseData = error?.response?.data || error?.data || {};
    const statusCode = Number(error?.response?.status ?? error?.status);
    const responseCode = Number(responseData?.code ?? error?.code);
    return (
      statusCode === METRIC_NO_ACCESS_STATUS ||
      responseCode === METRIC_NO_ACCESS_CODE
    );
  };

  const buildNoAccessMetricCards = (
    metrics: string[],
    granularity: string
  ): any[] => {
    return metrics.map((metricName) => ({
      metrics_name: metricName,
      granularity,
      noAccess: true,
      value: null,
    }));
  };

  const buildNoAccessCardsForGranularityGroups = (
    granularityGroups: Record<string, Set<string>>
  ): any[] => {
    const cardList: any[] = [];
    Object.entries(granularityGroups).forEach(
      ([granularity, metricsSet]) => {
        cardList.push(
          ...buildNoAccessMetricCards(Array.from(metricsSet), granularity)
        );
      }
    );
    return cardList;
  };

  // ========================================
  // AUTO-SAVE SETUP
  // ========================================
  const {
    autoSaveVisible,
    handleEdit: handleEditWithAutoSave,
    initializeAutoSave,
    handleAutoSaveCancel,
    handleAutoSaveConfirm,
    clearAutoSave,
  } = useWeeklyProjectHighlightAutoSave(
    isEditing,
    projects,
    pendingProjectDeletes,
    pendingContentDeletes,
    metricTextInfoMap,
    batchedMetricsDataMap,
    setProjects,
    setPendingProjectDeletes,
    setPendingContentDeletes,
    setOriginalProjects,
    setMetricTextInfoMap,
    setBatchedMetricsDataMap
  );

  // Memoize unsaved changes check for RoutePrompt to prevent unnecessary re-evaluations
  const hasUnsavedChanges = useMemo(() => {
    if (!isEditing) return false;
    const hasDataChanges = JSON.stringify(projects) !== JSON.stringify(originalProjects);
    const hasPendingProjectDeletes = pendingProjectDeletes.length > 0;
    const hasPendingContentDeletes = pendingContentDeletes.length > 0;
    return hasDataChanges || hasPendingProjectDeletes || hasPendingContentDeletes;
  }, [isEditing, projects, originalProjects, pendingProjectDeletes.length, pendingContentDeletes.length]);

  // ========================================
  // EDIT MODE HANDLERS
  // ========================================
  const handleEdit = () => {
    // Use auto-save handler which checks for restore prompt
    handleEditWithAutoSave(() => {
      // Track buried point for Weekly Project Highlight Edit button
      const btnPoint = createBtnPoint(
        ExecutiveSummaryTab,
        'weeklyProjectHighlightEdit',
        'Weekly Project Highlight Edit'
      );
      if (btnPoint) {
        SGBuriedBtnPoint(btnPoint);
      }

      // Reset temp ID counters when entering edit mode
      tempIdCounterRef.current = { project: 0, content: 0 };

      // Clear pending deletes when entering edit mode (fresh start)
      setPendingProjectDeletes([]);
      setPendingContentDeletes([]);

      // Store original data for comparison on save
      const clonedProjects = JSON.parse(JSON.stringify(projects)); // Deep clone
      setOriginalProjects(clonedProjects);

      // Initialize auto-save initial params - include metric maps for proper restore
      initializeAutoSave(clonedProjects, metricTextInfoMap, batchedMetricsDataMap);

      setIsEditing(true);
    });
  };

  const handleSave = async () => {
    try {
      // Set loading state for batch-update process
      setLoading(true);

      // Helper function to check if content item has changed
      const hasContentChanged = (currentContent: ProjectCard, originalContent?: ProjectCard): boolean => {
        if (!originalContent) return true; // New content
        // Compare the stringified data (includes documents array)
        return JSON.stringify(currentContent.data) !== JSON.stringify(originalContent.data);
      };

      // Helper function to check if content order has changed within a project
      const hasContentOrderChanged = (currentProject: Project, originalProject?: Project): boolean => {
        if (!originalProject) return false; // New projects are handled separately
        const currentOrder = currentProject.content_items.map(c => c.id).join(',');
        const originalOrder = originalProject.content_items.map(c => c.id).join(',');
        return currentOrder !== originalOrder;
      };

      // Initialize batch payload
      const batchPayload = {
        content_items_to_create: [] as Array<{ temp_id: string; type: string; value: string }>,
        content_items_to_update: [] as Array<{ id: number; type: string; value: string }>,
        content_items_to_delete: [] as number[],
        projects_to_create: [] as Array<{ temp_id: string; game_code: string; project_name?: string; order: number; content_orders: string }>,
        projects_to_update: [] as Array<{ id: number; order: number; content_orders: string }>,
        projects_to_delete: [] as number[],
      };

      // 1. Collect content items to delete (from pendingContentDeletes)
      const realContentIdsToDelete = pendingContentDeletes
        .filter(item => !String(item.id).startsWith('new-content-'))
        .map(item => parseInt(item.id));
      batchPayload.content_items_to_delete = realContentIdsToDelete;

      // 2. Collect projects to delete (from pendingProjectDeletes)
      const realProjectIdsToDelete = pendingProjectDeletes
        .filter(p => !String(p.id).startsWith('new-project-'))
        .map(p => parseInt(p.id));
      batchPayload.projects_to_delete = realProjectIdsToDelete;

      // Get all non-deleted projects to calculate order positions
      const allNonDeletedProjects = projects.filter(p => !pendingProjectDeletes.some(pd => pd.id === p.id));

      // 3. Process all projects to collect creates/updates
      for (const project of projects) {
        // Skip projects that are marked for deletion
        if (pendingProjectDeletes.some(p => p.id === project.id)) {
          continue;
        }

        const originalProject = originalProjects.find(op => op.id === project.id);
        const isNewProject = String(project.id).startsWith('new-project-');

        if (isNewProject) {
          // New project: collect for creation
          const contentOrders = JSON.parse(project.content_orders || '[]');

          // Filter out deleted content IDs from content_orders
          const filteredContentOrders = contentOrders.filter((contentId: any) => {
            const contentIdStr = String(contentId);
            // Check if this content ID is marked for deletion in this project
            const isDeleted = pendingContentDeletes.some(
              deleteItem => String(deleteItem.id) === contentIdStr && String(deleteItem.projectId) === String(project.id)
            );
            return !isDeleted;
          });

          // Calculate order based on current position in the array (descending order)
          const projectIndex = allNonDeletedProjects.findIndex(p => p.id === project.id);
          const calculatedOrder = allNonDeletedProjects.length - projectIndex;

          batchPayload.projects_to_create.push({
            temp_id: project.id,
            game_code: project.game_code,
            project_name: project.project_name,
            order: calculatedOrder,
            content_orders: JSON.stringify(filteredContentOrders) // Filtered to exclude deleted content IDs
          });

          // Collect all content items in this new project for creation
          for (const contentItem of project.content_items) {
            if (String(contentItem.id).startsWith('new-content-')) {
              batchPayload.content_items_to_create.push({
                temp_id: contentItem.id,
                type: contentItem.type,
                value: JSON.stringify(contentItem.data)
              });
            }
          }
        } else {
          // Existing project: ALWAYS include in projects_to_update with order and content_orders
          const contentOrders = JSON.parse(project.content_orders || '[]');

          // Filter out deleted content IDs from content_orders
          // Check if the content ID is in pendingContentDeletes for this project
          const filteredContentOrders = contentOrders.filter((contentId: any) => {
            const contentIdStr = String(contentId);
            // Check if this content ID is marked for deletion in this project
            const isDeleted = pendingContentDeletes.some(
              deleteItem => String(deleteItem.id) === contentIdStr && String(deleteItem.projectId) === String(project.id)
            );
            return !isDeleted;
          });

          // Calculate order based on current position in the array (descending order)
          const projectIndex = allNonDeletedProjects.findIndex(p => p.id === project.id);
          const calculatedOrder = allNonDeletedProjects.length - projectIndex;

          // Always include order in projects_to_update (required by API)
          batchPayload.projects_to_update.push({
            id: parseInt(project.id),
            order: calculatedOrder,
            content_orders: JSON.stringify(filteredContentOrders) // Filtered to exclude deleted content IDs
          });

          // Process content items in this project
          for (const contentItem of project.content_items) {
            const isNewContent = String(contentItem.id).startsWith('new-content-');

            if (isNewContent) {
              // New content item: collect for creation
              batchPayload.content_items_to_create.push({
                temp_id: contentItem.id,
                type: contentItem.type,
                value: JSON.stringify(contentItem.data)
              });
            } else {
              // Existing content item: check if changed
              const originalContentItem = originalProject?.content_items.find(oc => oc.id === contentItem.id);

              if (hasContentChanged(contentItem, originalContentItem)) {
                batchPayload.content_items_to_update.push({
                  id: parseInt(contentItem.id),
                  type: contentItem.type,
                  value: JSON.stringify(contentItem.data)
                });
              }
            }
          }
        }
      }

      console.log('Batch payload:', batchPayload);

      // Call batch-update API (single call only)
      const response = await batchUpdateProjectHighlight(batchPayload);

      if (response?.data?.success) {
        // Clear pending deletes
        setPendingProjectDeletes([]);
        setPendingContentDeletes([]);

        // Reset temp ID counters
        tempIdCounterRef.current = { project: 0, content: 0 };

        // Clear auto-save data on successful save
        clearAutoSave();

        // Refresh data from API to get the latest state with real IDs and correct ordering
        await refreshProjects(projectOptions);

        notification.success({
          description: 'Operate Successfully',
        });

        setIsEditing(false);
      } else {
        throw new Error(response?.data?.message || 'Failed to batch update');
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to batch update';
      notification.error({
        description: errorMessage,
      });
    } finally {
      // Always clear loading state, even if there's an error
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // Clear pending deletes
    setPendingProjectDeletes([]);
    setPendingContentDeletes([]);

    // Reset temp ID counters
    tempIdCounterRef.current = { project: 0, content: 0 };

    // Clear auto-save data when canceling
    clearAutoSave();

    // Restore original projects (discard changes)
    setProjects(JSON.parse(JSON.stringify(originalProjects))); // Deep clone
    setIsEditing(false);
  };


  // ========================================
  // MODAL HANDLERS
  // ========================================
  const handleAddProject = () => {
    setIsEditing(true);
    setIsAddProjectModalVisible(true);
  };

  const handleModalClose = () => {
    setIsAddProjectModalVisible(false);
    setSelectedProject('');
  };

  const handleNextStep = () => {
    if (selectedProject) {
      console.log('Selected project:', selectedProject);
      // Set mode to new and clear existing project data
      setExistingProjectData(undefined);
      // Close the project modal and open the content modal
      setIsAddProjectModalVisible(false);
      setContentModalMode('new');
      setIsAddContentModalVisible(true);
    }
  };

  const handleContentModalClose = () => {
    setIsAddContentModalVisible(false);
    setSelectedProject(''); // Reset selected project
    setContentModalMode('new'); // Reset mode
    setExistingProjectData(undefined); // Reset existing project data
    setEditingContent(null); // Reset editing content
  };

  // ========================================
  // CONTENT MANAGEMENT FUNCTIONS
  // ========================================
  // Function to generate temporary IDs
  const generateTempId = useCallback((type: 'project' | 'content'): string => {
    if (type === 'project') {
      tempIdCounterRef.current.project++;
      return `new-project-${tempIdCounterRef.current.project}`;
    } else {
      tempIdCounterRef.current.content++;
      return `new-content-${tempIdCounterRef.current.content}`;
    }
  }, []);

  const handleContentConfirm = async (result: {
    type: 'new-project' | 'add-content' | 'edit-content';
    projectId?: string;
    contentItems?: Array<{ id: string; type: string; data: any }>;
    contentOrders?: string;
    editedContent?: { id: string; type: string; data: any };
    // Already-fetched data from modal (to avoid duplicate API calls)
    metricTextInfo?: MetricTextInfo[];
    metricsData?: { card_list: any[]; trend_list: any[] };
    gameCode?: string;
  }) => {
    console.log('Content result:', result);
    try {
      if (result.type === 'new-project') {
        // Create new project with temp ID
        const { projectId, contentItems = [], contentOrders = '[]' } = result;

        // Get the next order
        const existingOrders = projects.map(p => p.order || 0);
        const maxOrder = existingOrders.length > 0 ? Math.max(...existingOrders) : 0;
        const nextOrder = maxOrder + 1;

        // Get project info from selectedProject
        const selectedProjectOpt = projectOptions.find((o) => o?.value === selectedProject) || {
          text: selectedProject,
          value: '0',
          type: '',
          cover: '',
        };

        // Create new project object
        const newProject: Project = {
          id: projectId!,
          game_code: selectedProjectOpt.value || '0',
          project_name: selectedProjectOpt.text || '',
          icon: selectedProjectOpt.cover || GameDefault,
          content_items: contentItems.map(item => ({
            id: item.id,
            type: item.type as 'dashboard' | 'ai_summary' | 'text',
            data: item.data
          })),
          order: nextOrder,
          content_orders: contentOrders
        };

        // Add to projects state at the top (new projects should always be at the top)
        // Since projects are sorted in descending order, highest order = top position
        setProjects(prevProjects => [newProject, ...prevProjects]);
      } else if (result.type === 'add-content') {
        // Add content to existing project
        const { projectId, contentItems = [], contentOrders } = result;

        setProjects(prevProjects => prevProjects.map(p => {
          if (String(p.id) === String(projectId)) {
            return {
              ...p,
              content_items: [...p.content_items, ...contentItems.map(item => ({
                id: item.id,
                type: item.type as 'dashboard' | 'ai_summary' | 'text',
                data: item.data
              }))],
              content_orders: contentOrders || p.content_orders
            };
          }
          return p;
        }));

        // Don't update originalProjects here - it should only be updated on save
        // originalProjects should remain as the baseline for comparison
      } else if (result.type === 'edit-content') {
        // Edit existing content
        const { editedContent, contentItems, contentOrders } = result;

        if (editedContent) {
          // Find the project containing this content
          setProjects(prevProjects => prevProjects.map(p => {
            const contentIndex = p.content_items.findIndex(item => item.id === editedContent.id);
            if (contentIndex !== -1) {
              // Update the content item
              const updatedContentItems = [...p.content_items];
              updatedContentItems[contentIndex] = {
                id: editedContent.id,
                type: editedContent.type as 'dashboard' | 'ai_summary' | 'text',
                data: editedContent.data
              };

              // If there are additional content items (for dashboard with multiple metrics)
              if (contentItems && contentItems.length > 1) {
                // Add new temp content items after the updated one
                const newItems = contentItems.slice(1).map(item => ({
                  id: item.id,
                  type: item.type as 'dashboard' | 'ai_summary' | 'text',
                  data: item.data
                }));
                updatedContentItems.splice(contentIndex + 1, 0, ...newItems);
              }

              return {
                ...p,
                content_items: updatedContentItems,
                content_orders: contentOrders || p.content_orders
              };
            }
            return p;
          }));

          // Don't update originalProjects here - it should only be updated on save
          // originalProjects should remain as the baseline for comparison
        }
      }

      // Update state with already-fetched data from modal (if provided)
      // This avoids duplicate API calls since the modal already fetches this data
      if (result.metricTextInfo && result.gameCode && result.metricsData) {
        // Update metricTextInfoMap with rainbow info (titles)
        setMetricTextInfoMap(prevMap => {
          const updatedMap = { ...prevMap };
          const existing = updatedMap[result.gameCode!] || [];

          // CRITICAL: For metrics like active_users that have multiple granularities,
          // we need to use composite key (value + granularity) for deduplication
          // Otherwise weekly active_users (WAU) will be treated as duplicate of daily (DAU)
          const existingKeys = new Set(
            existing.map(item =>
              item.granularity ? `${item.value}__${item.granularity}` : item.value
            )
          );

          // Add new items that don't exist yet (check by value + granularity composite key)
          const newItems = result.metricTextInfo!.filter(item => {
            const key = item.granularity ? `${item.value}__${item.granularity}` : item.value;
            return !existingKeys.has(key);
          });
          updatedMap[result.gameCode!] = [...existing, ...newItems];

          console.log(`✅ Updated metricTextInfoMap for ${result.gameCode}: total=${updatedMap[result.gameCode!].length}, new=${newItems.length}`);
          return updatedMap;
        });

        // Update batchedMetricsDataMap with metrics data (card_list and trend_list)
        setBatchedMetricsDataMap(prevMap => {
          const updatedMap = { ...prevMap };
          const existing = updatedMap[result.gameCode!] || { card_list: [], trend_list: [] };

          // CRITICAL: Use composite key (metrics_name + granularity) for deduplication
          // because the same metric can exist with different granularities (daily, weekly, monthly)
          // This must match how we RETRIEVE data later (line 1416-1418 checks both fields)
          const existingCardKeys = new Set(
            existing.card_list.map((item: any) => `${item.metrics_name}__${item.granularity || 'daily'}`)
          );

          // Add new card_list items that don't exist yet (check by metrics_name + granularity)
          const newCardItems = result.metricsData!.card_list.filter((item: any) =>
            !existingCardKeys.has(`${item.metrics_name}__${item.granularity || 'daily'}`)
          );

          // CRITICAL FIX: For trend_list, we need to MERGE metrics objects, not just deduplicate by date
          // Trend list structure: { date: "20251129", metrics: { metric1: {...}, metric2: {...} }, granularity: "daily" }
          // When adding new metrics to existing project, the NEW trend data has the new metrics,
          // but the EXISTING trend data has the old metrics for the same dates.
          // We must merge the metrics objects together!

          // Create a map of existing trend items by date + granularity
          const existingTrendMap = new Map(
            existing.trend_list.map((item: any) => [
              `${item.date}__${item.granularity || 'daily'}`,
              item
            ])
          );

          // Process new trend items: merge with existing or add new
          const mergedTrendList = [...existing.trend_list];

          result.metricsData!.trend_list.forEach((newItem: any) => {
            const key = `${newItem.date}__${newItem.granularity || 'daily'}`;
            const existingItem = existingTrendMap.get(key);

            if (existingItem) {
              // Merge the metrics objects: { ...existing.metrics, ...new.metrics }
              const mergedMetrics = {
                ...existingItem.metrics,
                ...newItem.metrics
              };

              // Update the existing item in the array
              const index = mergedTrendList.findIndex((item: any) =>
                `${item.date}__${item.granularity || 'daily'}` === key
              );
              if (index !== -1) {
                mergedTrendList[index] = {
                  ...existingItem,
                  metrics: mergedMetrics
                };
              }

              console.log(`🔄 Merged trend data for ${key}: added ${Object.keys(newItem.metrics).length} new metrics to ${Object.keys(existingItem.metrics).length} existing metrics`);
            } else {
              // Add completely new trend item
              mergedTrendList.push(newItem);
              console.log(`➕ Added new trend item for ${key} with ${Object.keys(newItem.metrics).length} metrics`);
            }
          });

          updatedMap[result.gameCode!] = {
            card_list: [...existing.card_list, ...newCardItems],
            trend_list: mergedTrendList
          };

          console.log(`✅ Updated batchedMetricsDataMap for ${result.gameCode}: card_list=${updatedMap[result.gameCode!].card_list.length}, trend_list=${updatedMap[result.gameCode!].trend_list.length}`);

          return updatedMap;
        });
      }

      // Show success message
      notification.success({
        description: 'Operate Successfully',
      });
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to update content';
      notification.error({
        description: errorMessage,
      });
      // Re-throw so the modal's error handler can catch it
      throw error;
    }
  };

  const handleAddContentToProject = (project: Project) => {
    // Set the selected project and open the content modal
    // Check if this is a customized project (game_code is empty or '0')
    const isCustomizedProject = !project.game_code || project.game_code === '0';

    if (isCustomizedProject) {
      // For customized projects, set the project name as selectedProject
      setSelectedProject(project.project_name);
    } else {
      // For regular projects, set the game_code
      setSelectedProject(project.game_code);
    }

    console.log('project for Add Content Modal:', project);
    console.log('Selected project for Add Content Modal:', selectedProject);

    // Set mode to existing and pass only the specific data we need
    setContentModalMode('existing');
    setExistingProjectData({
      id: parseInt(project.id),
      game_code: project.game_code,
      project_name: project.project_name,
      order: project.order || 0,
      content_orders: project.content_orders || '[]'
    });

    setIsAddContentModalVisible(true);
  };

  const handleEditContent = (contentId: string, projectId: string) => {
    console.log('handleEditContent', contentId, projectId);

    // Find the content item to edit
    const project = projects.find(p => p.id === projectId);
    if (!project) {
      // messageTips('error', 'Project not found');
      notification.error({
        description: 'Project not found',
      });
      return;
    }

    // Set modal mode to existing (edit mode)
    // setContentModalMode('existing');
    // Set existing project data for editing (needed for dashboard content editing)
    setExistingProjectData({
      id: parseInt(project.id),
      game_code: project.game_code,
      project_name: project.project_name,
      order: project.order || 0,
      content_orders: project.content_orders || '[]'
    });

    const contentItem = project.content_items.find(item => item.id === contentId);
    if (!contentItem) {
      // messageTips('error', 'Content item not found');
      notification.error({
        description: 'Content item not found',
      });
      return;
    }

    // Set the project for editing
    // Check if this is a customized project (game_code is '0')
    const isCustomizedProject = project.game_code === '0';

    if (isCustomizedProject) {
      // For customized projects, set the project name as selectedProject
      setSelectedProject(project.project_name);
    } else {
      // For regular projects, set the game_code
      setSelectedProject(project.game_code);
    }

    // Set the content data for editing
    setEditingContent({
      id: contentId,
      type: contentItem.type,
      data: contentItem.data
    });

    // Open the modal
    setIsAddContentModalVisible(true);
  };

  // ========================================
  // DELETE FUNCTIONS
  // ========================================
  const handleDeleteProject = async (projectId: string) => {
    console.log('handleDeleteProject, projectId: ', projectId);

    const confirmed = await Modal.confirm({
      className: 'dbs-delete-confirm-modal',
      message: `Are you sure you want to delete this project?`,
      description: 'All content under this category will also be deleted.',
      okText: 'Confirm',
      cancelText: 'Cancel',
    });

    if (confirmed) {
      // Find the project to delete
      const projectToDelete = projects.find(p => p.id === projectId);

      if (projectToDelete) {
        // Only track real projects (not temp IDs that were never saved)
        if (!String(projectId).startsWith('new-project-')) {
          setPendingProjectDeletes(prev => [...prev, projectToDelete]);
        }

        // Remove from UI immediately
        setProjects(prevProjects => prevProjects.filter(p => p.id !== projectId));
        setOriginalProjects(prevOriginal => prevOriginal.filter(p => p.id !== projectId));
      }
    }
  };

  const handleDeleteContent = async (contentId: string, projectId: string) => {
    console.log('handleDeleteContent, projectId: ', projectId, 'contentId: ', contentId);

    const confirmed = await Modal.confirm({
      className: 'dbs-delete-confirm-modal',
      message: `Are you sure you want to delete the content?`,
      okText: 'Confirm',
      cancelText: 'Cancel',
    });

    if (confirmed) {
      // Find the project
      const project = projects.find(p => p.id === projectId);
      if (project) {
        // Only track real content items (not temp IDs that were never saved)
        if (!String(contentId).startsWith('new-content-')) {
          setPendingContentDeletes(prev => [...prev, { id: contentId, projectId }]);
        }

        // Update the project's content_orders to remove the deleted content
        const existingContentOrders = JSON.parse(project.content_orders || '[]');
        // Filter out the deleted content ID (handle both number and string IDs)
        const updatedContentOrders = existingContentOrders.filter((id: any) => {
          // Handle both numeric IDs and string IDs (including temp IDs)
          return String(id) !== String(contentId);
        });

        // Update local state immediately (UI update)
        setProjects(prevProjects => prevProjects.map(p => {
          if (p.id === projectId) {
            return {
              ...p,
              content_items: p.content_items.filter(item => item.id !== contentId),
              content_orders: JSON.stringify(updatedContentOrders)
            };
          }
          return p;
        }));

        setOriginalProjects(prevOriginal => prevOriginal.map(p => {
          if (p.id === projectId) {
            return {
              ...p,
              content_items: p.content_items.filter(item => item.id !== contentId),
              content_orders: JSON.stringify(updatedContentOrders)
            };
          }
          return p;
        }));
      }
    }
  };

  // ========================================
  // FILE CHANGE HANDLER (for edit mode)
  // ========================================
  const handleFilesChange = (contentId: string, projectId: string, updatedFiles: any[]) => {
    console.log('🔄 handleFilesChange called', { contentId, projectId });
    console.log('📁 Updated files:', updatedFiles);
    console.log('📁 File names:', updatedFiles.map(f => f.name));

    // Update the local state immediately for better UX
    setProjects(prevProjects => {
      const newProjects = prevProjects.map(project => {
        if (project.id === projectId) {
          return {
            ...project,
            content_items: project.content_items.map(contentItem => {
              if (contentItem.id === contentId) {
                console.log('✅ Updating content item data with new files');
                console.log('Old documents:', (contentItem.data as any).documents);
                console.log('New documents:', updatedFiles);
                // Update the documents field in the content item's data
                return {
                  ...contentItem,
                  data: {
                    ...contentItem.data,
                    documents: updatedFiles
                  } as any
                };
              }
              return contentItem;
            })
          };
        }
        return project;
      });
      console.log('Updated projects state');
      return newProjects;
    });
  };

  // ========================================
  // EVENT HANDLERS & UTILITIES
  // ========================================
  const handleRowClick = (gameCode: string, metricName?: string, granularity: 'daily' | 'weekly' | 'monthly' = 'daily', metricKey?: string) => {
    // Track buried point for dashboard metric header click
    const btnPoint = createBtnPoint(
      ExecutiveSummaryTab,
      'dashboardMetricHeaderClick',
      `Dashboard Metric Header Click - Game: ${gameCode}${metricName ? `, Metric: ${metricName}` : ''}, Granularity: ${granularity}`
    );
    if (btnPoint) {
      SGBuriedBtnPoint(btnPoint);
    }

    history.push(getDashboardOverviewPath(gameCode, granularity, metricKey));
  };

  // Drag and drop handlers
  const handleProjectDragEnd = (result: any) => {
    if (!result.destination) return;

    const sourceIndex = result.source.index;
    const destIndex = result.destination.index;

    if (sourceIndex === destIndex) return; // No change

    // Reorder the filtered projects (what the user sees)
    const reorderedFiltered = Array.from(filteredProjects);
    const [movedProject] = reorderedFiltered.splice(sourceIndex, 1);
    reorderedFiltered.splice(destIndex, 0, movedProject);

    // Update order fields based on new positions (descending order)
    const updatedFiltered = reorderedFiltered.map((project, index) => ({
      ...project,
      order: reorderedFiltered.length - index
    }));

    // If no filtering (showing all projects), just update projects directly
    if (filteredProjects.length === projects.length) {
      setProjects(updatedFiltered);
      return;
    }

    // If filtering is active: keep non-filtered projects, insert reordered filtered projects
    // Get IDs of filtered projects
    const filteredIds = new Set(filteredProjects.map(p => p.id));

    // Separate non-filtered projects
    const nonFilteredProjects = projects.filter(p => !filteredIds.has(p.id));

    // Combine: non-filtered projects first, then reordered filtered projects
    // (You can adjust the merge strategy based on your needs)
    const newProjects = [...nonFilteredProjects, ...updatedFiltered];

    setProjects(newProjects);
  };

  const handleSortContentWithinProject = (projectId: string, newCards: ProjectCard[]) => {
    setProjects(prevProjects =>
      prevProjects.map(project => {   // Loop through ALL projects
        if (project.id === projectId) {    // Find the ONE project we're sorting
          // Update content_items and content_orders
          const contentOrders = newCards.map(card => {
            // Keep existing IDs as numbers, temp IDs as strings
            const id = String(card.id);
            return id.startsWith('new-content-') ? id : parseInt(id);
          });
          return {
            ...project,
            content_items: newCards, // ✅ Create new project with sorted cards
            content_orders: JSON.stringify(contentOrders) // Update content_orders with new order
          };
        }
        return project;
      })
    );
  };


  const transformProjectData = (apiProjects: any[], gameOptions: any[] = []): Project[] => {
    return apiProjects.map(project => {
      const contentOrders = JSON.parse(project.content_orders || '[]');
      const transformedItems = project.content_items.map((item: any) => {
        const value = JSON.parse(item.value || '{}');
        // console.log("Transforming item:", item, "with value:", value);

        let data: ContentData;

        switch (item.type) {
          case 'dashboard':
            data = {
              metrics: value.metrics || []
            } as DashboardContentData;
            break;

          case 'ai_summary':
            data = {
              prompt: value.prompt || '',
              agent: value.agent || 'chatGPT',
              documents: value.documents || []
            } as AISummaryContentData;
            break;

          case 'text':
            data = {
              textType: value.textType || 'long',
              textTitle: value.textTitle || '',
              textContent: value.textContent || '',
              documents: value.documents || []
            } as TextContentData;
            break;

          default:
            // messageTips('error', 'Unknown content type: ' + item.type);
            notification.error({
              description: 'Unknown content type: ' + item.type,
            });
            // Fallback for unknown types - you might want to handle this differently
            data = {
              textType: value.textType || 'long',
              textTitle: value.textTitle || '',
              textContent: value.textContent || '',
              documents: value.documents || []
            } as TextContentData;
        }

        return {
          id: String(item.id),
          type: item.type as 'dashboard' | 'ai_summary' | 'text',
          data
        };
      });

      // Sort
      // const sortedItems = [...transformedItems].sort((a, b) => {
      //   return contentOrders.indexOf(Number(a.id)) - contentOrders.indexOf(Number(b.id));
      // });

      // Get game icon from the passed game options
      // console.log('gameOptions', gameOptions);
      // console.log('project.game_code: ', project.game_code);
      const game = gameOptions.find(g => g.value === project.game_code);
      const gameIcon = game?.cover;

      return {
        id: String(project.id),
        game_code: project.game_code,
        project_name: project.project_name || project.game_code,
        icon: gameIcon,
        content_items: transformedItems,
        order: project.order,
        content_orders: project.content_orders,
        created_time: project.created_time,
      };
    });
  };

  // ========================================
  // COMPUTED VALUES
  // ========================================
  // Derive selected project option, memoized to avoid unnecessary re-computation
  const selectedProjectOpt = useMemo(() => {
    let opt = projectOptions.find((o) => o?.value === selectedProject);

    // If selectedProject is not in projectOptions (customized project), create a fallback option
    if (!opt && selectedProject) {
      opt = {
        text: selectedProject,
        value: '0',
        type: '',
        cover: '',
        // disabled: false,
      };
    }

    console.log('Selected Project Option: ', opt);
    return opt;
  }, [projectOptions, selectedProject]);

  // ========================================
  // API/DATA FETCHING FUNCTIONS
  // ========================================
  // this fetchGameList function is for the project dropdown when adding a new project
  const fetchGameList = async () => {
    try {
      const response = await getGameList();
      console.log('Game List response from dashboard: ', response);
      const raw = Array.isArray(response) ? response : [];

      // Set project options for dropdown (includes cover for icons)
      const games = raw.map((game: any) => ({
        text: (isEn ? game.game_name : (game.game_cn_name || game.game_name)).trim(),
        value: game.game_code,
        type: game.data_frame,
        cover: game.cover,
        // disabled: !game.is_access,
      })).sort((a, b) => {
        // Sort in ascending order (alphabetically and numerically)
        // Game 1 → Game 2 → Game 10 (not Game 1 → Game 10 → Game 2)
        return a.text.localeCompare(b.text, undefined, { numeric: true, sensitivity: 'base' });
      });
      // console.log('Sorted games:', games.map(g => g.text));
      setProjectOptions(games);

      // Return the games for immediate use
      return games;
    } catch (err: any) {
      // messageTips('error', 'Failed to fetch game list');
      notification.error({
        description: 'Failed to fetch game list',
      });
      // Don't show error message for game list as it's not critical
      return [];
    }
  };

  // Fetch metric text info for a specific game code with granularity-aware Rainbow Info matching
  const fetchMetricTextInfo = useCallback(async (
    gameCode: string,
    metricsByGranularity: Record<string, string[]>, // { daily: ['active_users', ...], weekly: [...], monthly: [...] }
    gameDataFrame?: string
  ) => {
    try {
      // Check if we have any metrics
      const hasMetrics = Object.values(metricsByGranularity).some(metrics => metrics.length > 0);
      if (!hasMetrics) {
        return [];
      }

      // Determine rainbow game_code based on data_frame
      // If data_frame is 'pc', use 'pc_demo'; if 'mobile', use 'demo'
      // (as instructed by 经分前端 因为七彩石 没有所有项目的指标都配， 所以只配了pc_demo和demo)
      let rainbowGameCode = gameCode; // Default to original game code
      if (gameDataFrame === 'pc') {
        rainbowGameCode = 'pc_demo';
      } else if (gameDataFrame === 'mobile') {
        rainbowGameCode = 'demo';
      }

      console.log(`🎮 Fetching rainbow info with game_code: ${rainbowGameCode} (original: ${gameCode}, data_frame: ${gameDataFrame})`);

      // Fetch metric text info (text_en and text_cn) using rainbow info API
      const textInfoResponse = await getDashboardRainbowInfo({
        game_code: rainbowGameCode,
        folder: 'metric_info'
      });

      // Use shared utility to process Rainbow Info with granularity awareness
      const processedTextInfo = processRainbowInfo(textInfoResponse, metricsByGranularity);
      return processedTextInfo;
    } catch (error: any) {
      console.error(`Error fetching metric text info for ${gameCode}:`, error);
      return [];
    }
  }, []);

  const refreshProjects = useCallback(async (gameOptions: any[] = []) => {
    try {
      setLoading(true);

      // API no longer requires date filtering - fetch all projects
      const response = await getProjectHighlights({});
      // console.log('Project Highlights response: ', response);
      const transformedProjects = transformProjectData(response.data.projects, gameOptions);
      console.log('Transformed Projects: ', transformedProjects);
      // Sort projects in descending order by order
      const sortedProjects = transformedProjects.sort((a, b) => Number(b.order) - Number(a.order));
      setProjects(sortedProjects);
      // Also update original projects when fetching fresh data
      setOriginalProjects(JSON.parse(JSON.stringify(sortedProjects)));

      // ========================================
      // BATCH METRICS DATA FETCHING
      // ========================================
      // Extract all metrics from projects' content items, grouped by game_code AND granularity
      const metricsByGameCodeAndGranularity: Record<string, Record<string, Set<string>>> = {};
      sortedProjects.forEach(project => {
        if (!metricsByGameCodeAndGranularity[project.game_code]) {
          metricsByGameCodeAndGranularity[project.game_code] = {};
        }
        project.content_items.forEach(card => {
          if (card.type === 'dashboard') {
            const dashboardData = card.data as DashboardContentData;
            dashboardData.metrics.forEach(metric => {
              // Get granularity from metric, default to 'daily' for backward compatibility
              const granularity = metric.granularity || 'daily';

              if (!metricsByGameCodeAndGranularity[project.game_code][granularity]) {
                metricsByGameCodeAndGranularity[project.game_code][granularity] = new Set();
              }
              metricsByGameCodeAndGranularity[project.game_code][granularity].add(metric.metrics_name);
            });
          }
        });
      });

      console.log('📊 Metrics by game code and granularity:', metricsByGameCodeAndGranularity);

      // Fetch metric text info for each game code with its specific metrics
      const uniqueGameCodes = Object.keys(metricsByGameCodeAndGranularity);
      const metricTextInfoPromises = uniqueGameCodes.map(async (gameCode) => {
        const granularityData = metricsByGameCodeAndGranularity[gameCode];

        // Convert Sets to Arrays for each granularity
        const metricsByGranularity: Record<string, string[]> = {};
        Object.entries(granularityData).forEach(([granularity, metricsSet]) => {
          metricsByGranularity[granularity] = Array.from(metricsSet);
        });

        // Only fetch if there are metrics for this game_code
        const hasMetrics = Object.values(metricsByGranularity).some(metrics => metrics.length > 0);
        if (!hasMetrics) {
          return { gameCode, textInfo: [] };
        }

        // Find the data_frame for this game_code from gameOptions
        const gameOption = gameOptions.find(game => game.value === gameCode);
        const dataFrame = gameOption?.type; // 'pc' or 'mobile'

        const textInfo = await fetchMetricTextInfo(gameCode, metricsByGranularity, dataFrame);
        return { gameCode, textInfo };
      });
      const metricTextInfoResults = await Promise.all(metricTextInfoPromises);
      const newMetricTextInfoMap: Record<string, MetricTextInfo[]> = {};
      metricTextInfoResults.forEach(({ gameCode, textInfo }) => {
        newMetricTextInfoMap[gameCode] = textInfo;
      });
      setMetricTextInfoMap(newMetricTextInfoMap);
      console.log('Metric text info map updated:', newMetricTextInfoMap);

      // ========================================
      // BATCH FETCH METRICS DATA
      // ========================================
      // Fetch metrics data for each game code in batches
      const metricsDataPromises = uniqueGameCodes.map(async (gameCode) => {
        const granularityGroups = metricsByGameCodeAndGranularity[gameCode];
        if (!granularityGroups || Object.keys(granularityGroups).length === 0) {
          return { gameCode, data: { card_list: [], trend_list: [] } };
        }

        // Set loading state for this game code
        setMetricsLoading(prev => ({ ...prev, [gameCode]: true }));

        try {
          console.log(`🔄 Fetching metrics for ${gameCode} across multiple granularities`);

          // Fetch metrics for each granularity separately using shared date utilities
          const granularityPromises = Object.entries(granularityGroups).map(async ([granularity, metricsSet]) => {
            const metrics = Array.from(metricsSet);

            // Get date range using shared utility
            const dateRange = getDateRangeByGranularity(granularity as 'daily' | 'weekly' | 'monthly');

            let apiFunction;
            let requestData: any;

            if (granularity === 'daily') {
              // for daily, we will spcial handle full_game_asp metrics as it need additional params when calling api
              apiFunction = getDashboardCardMetrics;

              // Check if full_game_asp is in the metrics array
              const hasFullGameAsp = metrics.includes('full_game_asp');
              const otherMetrics = metrics.filter((m: string) => m !== 'full_game_asp');

              if (hasFullGameAsp) {
                // Need to make separate API calls
                const promises = [];

                // API call for full_game_asp with is_confirm: true
                const aspRequestData = {
                  game_code: gameCode,
                  period: 'daily',
                  start_time: dateRange.start_time,
                  end_time: dateRange.end_time,
                  metrics: ['full_game_asp'],
                  trend_metrics: ['full_game_asp'],
                  is_confirm: true
                };
                promises.push(apiFunction(aspRequestData));

                // API call for other metrics (if any) without is_confirm
                if (otherMetrics.length > 0) {
                  const othersRequestData = {
                    game_code: gameCode,
                    period: 'daily',
                    start_time: dateRange.start_time,
                    end_time: dateRange.end_time,
                    metrics: otherMetrics,
                    trend_metrics: otherMetrics
                  };
                  promises.push(apiFunction(othersRequestData));
                }

                // Wait for both API calls
                const results = await Promise.all(promises);

                // Merge the results
                // For card_list: simple concatenation is fine
                const mergedCardList = results.flatMap(r => r?.data?.card_list || []);

                // For trend_list: merge by date to avoid duplicate date entries
                const trendMap = new Map();
                results.forEach(result => {
                  (result?.data?.trend_list || []).forEach((trendItem: any) => {
                    const key = trendItem.date;
                    if (trendMap.has(key)) {
                      // Merge metrics objects for the same date
                      const existing = trendMap.get(key);
                      trendMap.set(key, {
                        ...existing,
                        metrics: {
                          ...existing.metrics,
                          ...trendItem.metrics
                        }
                      });
                    } else {
                      trendMap.set(key, trendItem);
                    }
                  });
                });
                const mergedTrendList = Array.from(trendMap.values());

                // Attach granularity and return
                const card_list = mergedCardList.map((metric: any) => ({
                  ...metric,
                  granularity
                }));

                const trend_list = mergedTrendList.map((trend: any) => ({
                  ...trend,
                  granularity
                }));

                return { card_list, trend_list };
              } else {
                // No full_game_asp, use regular single API call
                requestData = {
                  game_code: gameCode,
                  period: 'daily',
                  start_time: dateRange.start_time,
                  end_time: dateRange.end_time,
                  metrics,
                  trend_metrics: metrics
                };
              }
            } else if (granularity === 'weekly') {
              apiFunction = getWeeklyCardMetrics;
              requestData = {
                game_code: gameCode,
                period: 'weekly' as 'weekly',
                start_time: dateRange.start_time,
                end_time: dateRange.end_time,
                metrics,
                trend_metrics: metrics
              };
            } else if (granularity === 'monthly') {
              apiFunction = getMonthlyCardMetrics;
              requestData = {
                game_code: gameCode,
                period: 'monthly' as 'monthly',
                start_time: dateRange.start_time,
                end_time: dateRange.end_time,
                metrics,
                trend_metrics: metrics
              };
            } else {
              // Unknown granularity - default to daily using shared utility
              console.warn(`Unknown granularity: ${granularity}, defaulting to daily`);
              const defaultDateRange = getDateRangeByGranularity('daily');
              apiFunction = getDashboardCardMetrics;
              requestData = {
                game_code: gameCode,
                period: 'daily',
                start_time: defaultDateRange.start_time,
                end_time: defaultDateRange.end_time,
                metrics,
                trend_metrics: metrics
              };
            }

            // Only execute the single API call if we didn't already handle full_game_asp above
            if (granularity === 'daily') {
              // For daily with full_game_asp, we already returned above
              // This block handles daily without full_game_asp
            }

            console.log(`  📊 Fetching ${granularity} metrics:`, metrics);
            const response = await apiFunction(requestData);

            // Attach granularity to each metric in the response
            const card_list = (response?.data?.card_list || []).map((metric: any) => ({
              ...metric,
              granularity
            }));

            const trend_list = (response?.data?.trend_list || []).map((trend: any) => ({
              ...trend,
              granularity
            }));

            return { card_list, trend_list };
          });

          // Wait for all granularity requests to complete
          const granularityResults = await Promise.all(granularityPromises);

          // Merge all results for this game code
          const mergedCardList: any[] = [];
          const mergedTrendList: any[] = [];

          granularityResults.forEach(result => {
            mergedCardList.push(...result.card_list);
            mergedTrendList.push(...result.trend_list);
          });

          console.log(`✅ Fetched ${mergedCardList.length} metrics for ${gameCode}`);

          return {
            gameCode,
            data: {
              card_list: mergedCardList,
              trend_list: mergedTrendList
            }
          };
        } catch (error: any) {
          console.error(`❌ Failed to fetch metrics for ${gameCode}:`, error);
          if (isMetricNoAccessError(error)) {
            return {
              gameCode,
              data: {
                card_list: buildNoAccessCardsForGranularityGroups(granularityGroups),
                trend_list: [],
              },
            };
          }
          notification.error({
            description: `Failed to fetch metrics for ${gameCode}: ${error?.response?.data?.message || error?.message}`,
          });
          return { gameCode, data: { card_list: [], trend_list: [] } };
        } finally {
          setMetricsLoading(prev => ({ ...prev, [gameCode]: false }));
        }
      });

      const metricsDataResults = await Promise.all(metricsDataPromises);
      const newBatchedMetricsDataMap: Record<string, { card_list: any[], trend_list: any[] }> = {};
      metricsDataResults.forEach(({ gameCode, data }) => {
        newBatchedMetricsDataMap[gameCode] = data;
      });
      setBatchedMetricsDataMap(newBatchedMetricsDataMap);
      console.log('✅ Batched metrics data map updated:', newBatchedMetricsDataMap);
    } catch (err: any) {
      // Show backend error message
      const errorMessage = err.response?.data?.message || err.message || 'Failed to refresh projects';
      // messageTips('error', errorMessage);
      notification.error({
        description: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  }, [fetchMetricTextInfo]);

  const filteredProjects = projects;

  // ========================================
  // EFFECTS
  // ========================================
  useEffect(() => {
    // Fetch game list first, then refresh projects with the game data
    const initializeData = async () => {
      const gameOptions = await fetchGameList();
      await refreshProjects(gameOptions);
    };

    initializeData();
  }, [isEn]); // refresh when language changes


  return (
    <div className="dbs-weekly-projects-highlight-wrap" style={{ position: 'relative' }}>
      {/* Loading overlay during batch-update */}
      {loading && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          borderRadius: '4px'
        }}>
          <Loading classNameValue="dbs-loading" />
        </div>
      )}

      {/* Project Card */}
      <CardCommon
        hasEditMode={true}
        canEdit={canEdit}
        isEditing={isEditing}
        title={rendTitle({
          title: formatMessage({ id: 'dbs_weekly_projects_highlight' }),
          tips: (() => {
            // Get current time in UTC+8
            const nowUTC8 = moment().utcOffset(8);
            const currentHour = nowUTC8.hour();

            // If before 5 PM (17:00), show T-2, otherwise show T-1
            const daysAgo = currentHour < 17 ? 2 : 1;
            const updateDate = moment().subtract(daysAgo, 'days').format('YYYY-MM-DD');

            return `Updated on ${updateDate} | The data for console games may be delayed. Sales-related data is updated daily at 5 PM (UTC+8).`;
          })()
        })}
        onEdit={handleEdit}
        onSave={handleSave}
        onCancel={handleCancel}
      >
        {/* Add New Project Button */}
        <div className="dbs-weekly-controls">
          {canEdit && isEditing && (
            <Button
              type='weak'
              onClick={handleAddProject}
              className="dbs-add-project-btn"
              style={{ marginBottom: '20px' }}
            >
              <img src={PlusIcon} alt="Add" className="dbs-add-project-icon" />
              {formatMessage({ id: 'dbs_add_project' })}
            </Button>
          )}
        </div>

        <rbd.DragDropContext onDragEnd={handleProjectDragEnd}>
          <rbd.Droppable droppableId="projects">
            {(provided: any) => (
              <div className="dbs-project-card-wrap" {...provided.droppableProps} ref={provided.innerRef}>
                {filteredProjects.length === 0 ? (
                  <div className="dbs-weekly-no-data">
                    {canEdit ? (
                      <div>
                        No Data. Please
                        <span onClick={handleAddProject} style={{ cursor: 'pointer', color: '#7F6FF1', marginLeft: '4px' }}>Add new project</span>
                      </div>
                    ) : (
                      <div>No Data</div>
                    )}
                  </div>
                ) : (
                  filteredProjects.map((project, projectIndex) => (
                    <rbd.Draggable
                      key={project.id}
                      draggableId={project.id}
                      index={projectIndex}
                      isDragDisabled={!isEditing}
                    >
                      {(provided: any, snapshot: any) => (
                        <div
                          className="dbs-project-drag-handle-wrapper"
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                        >
                          {canEdit && isEditing && (
                            <div
                              className="dbs-project-drag-handle"
                              {...provided.dragHandleProps}
                            >
                              <img src={DragHandle} alt="Drag handle" />
                            </div>
                          )}
                          <div
                            className={`dbs-project-card ${snapshot.isDragging ? 'dragging' : ''}`}
                            data-project-id={project.id}
                          >
                            {canEdit && isEditing && (
                              <div className="dbs-project-card-actions">
                                <Button
                                  type="text"
                                  onClick={() => handleAddContentToProject(project)}
                                  className="dbs-add-content-btn"
                                >
                                  Add Content
                                </Button>
                                <Button
                                  type="text"
                                  onClick={() => handleDeleteProject(project.id)}
                                  className="dbs-delete-project-btn"
                                >
                                  Delete
                                </Button>
                              </div>
                            )}
                            <Row>
                              <Col span={2}>
                                <div className="project-title-wrapper">
                                  <img className="dbs-project-icon" src={project.icon || GameDefault} alt={`${project.game_code} logo`} />
                                  <div className="dbs-main-title">{project.project_name}</div>
                                </div>
                              </Col>
                              <ReactSortable
                                list={project.content_items}
                                setList={(newCards) => handleSortContentWithinProject(project.id, newCards)}
                                disabled={!isEditing}
                                animation={200}
                                handle=".dbs-content-card-drag-handle, .dbs-metrics-drag-handle"
                                className="dbs-project-cards-items-wrapper"
                              >
                                {project.content_items.flatMap((card) => {
                                  if (card.type === 'dashboard') {
                                    // For dashboard cards, create a separate item for each metric
                                    return (card.data as DashboardContentData).metrics.map((metric, metricIndex) => {
                                      const metricGranularity = metric.granularity || 'daily'; // Get granularity first
                                      const metricInfo = findMetricInfo(metric.metrics_name, metricTextInfoMap[project.game_code] || [], metricGranularity);
                                      const displayName = metricInfo
                                        // ? (locale === 'zh-CN' ? metricInfo.text_cn : metricInfo.text_en) // tempoarary disable chinese name
                                        ? metricInfo.text_en
                                        : metric.metrics_name;

                                      // Get batched data for this metric - match by BOTH metrics_name AND granularity
                                      const gameCodeData = batchedMetricsDataMap[project.game_code];

                                      const metricCardData = gameCodeData?.card_list?.find((item: any) =>
                                        item.metrics_name === metric.metrics_name && item.granularity === metricGranularity
                                      );
                                      // IMPORTANT: Filter trend_list by granularity to avoid mixing daily/weekly/monthly data
                                      const trendList = (gameCodeData?.trend_list || []).filter((trend: any) =>
                                        trend.granularity === metricGranularity
                                      );

                                      const isLoadingMetrics = metricsLoading[project.game_code] || false;

                                      return (
                                        <DashboardMetricCard
                                          key={`${card.id}-${metricIndex}`}
                                          metric={metric}
                                          gameCode={project.game_code}
                                          locale={locale}
                                          isEditing={isEditing}
                                          metricTextInfo={metricTextInfoMap[project.game_code] || []}
                                          batchedMetricData={metricCardData}
                                          batchedTrendList={trendList}
                                          isLoading={isLoadingMetrics}
                                          onHeaderClick={() => handleRowClick(project.game_code, displayName, metricGranularity as 'daily' | 'weekly' | 'monthly', metric.metrics_name)}
                                          onEdit={() => handleEditContent(card.id, project.id)}
                                          onDelete={() => handleDeleteContent(card.id, project.id)}
                                        />
                                      );
                                    });
                                  } else {
                                    // For other card types, create a single item
                                    return (
                                      // if its a text card and the text type is short, then it takes 1/6 of the visible area, otherwise it takes 2/6 of the visible area
                                      <div key={card.id} className={card.type === 'text' && (card.data as TextContentData).textType === 'short' ? 'dbs-sortable-item-1-6' : 'dbs-sortable-item-2-6'}>
                                        {card.type === 'ai_summary' ? (
                                          <ContentCard
                                            title="AI Summary"
                                            type="ai"
                                            items={[(card.data as AISummaryContentData).prompt]}
                                            files={(card.data as AISummaryContentData).documents || []}
                                            mode={isEditing ? 'edit' : 'normal'}
                                            onEdit={() => handleEditContent(card.id, project.id)}
                                            onDelete={() => handleDeleteContent(card.id, project.id)}
                                            onFilesChange={(files) => handleFilesChange(card.id, project.id, files)}
                                            domain="weekly_project_highlight"
                                            itemId={project.id} // Pass project ID for scroll restoration
                                            urlSearchParams={{
                                              game_code: project.game_code,
                                              project_id: project.id,
                                              content_id: card.id
                                            }}
                                          // logKeys={{
                                          //   previewLogKey: 'WEEKLY_PROJECT_HIGHLIGHT_DOCUMENT_PREVIEW_SUBMIT',
                                          //   downloadLogKey: 'WEEKLY_PROJECT_HIGHLIGHT_DOCUMENT_DOWNLOAD_SUBMIT',
                                          //   shareLogKey: 'WEEKLY_PROJECT_HIGHLIGHT_DOCUMENT_SHARE_SUBMIT',
                                          // }}
                                          />
                                        ) : (
                                          <ContentCard
                                            title={(card.data as TextContentData).textTitle}
                                            type="default"
                                            variant="no-bullet"
                                            renderHtml={true}
                                            items={[(card.data as TextContentData).textContent]}
                                            files={(card.data as TextContentData).documents || []}
                                            mode={isEditing ? 'edit' : 'normal'}
                                            onEdit={() => handleEditContent(card.id, project.id)}
                                            onDelete={() => handleDeleteContent(card.id, project.id)}
                                            onFilesChange={(files) => handleFilesChange(card.id, project.id, files)}
                                            domain="weekly_project_highlight"
                                            itemId={project.id} // Pass project ID for scroll restoration
                                            urlSearchParams={{
                                              game_code: project.game_code,
                                              project_id: project.id,
                                              content_id: card.id
                                            }}
                                          // logKeys={{
                                          //   previewLogKey: 'WEEKLY_PROJECT_HIGHLIGHT_DOCUMENT_PREVIEW_SUBMIT',
                                          //   downloadLogKey: 'WEEKLY_PROJECT_HIGHLIGHT_DOCUMENT_DOWNLOAD_SUBMIT',
                                          //   shareLogKey: 'WEEKLY_PROJECT_HIGHLIGHT_DOCUMENT_SHARE_SUBMIT',
                                          // }}
                                          />
                                        )}
                                      </div>
                                    );
                                  }
                                })}
                              </ReactSortable>
                            </Row>
                          </div>
                        </div>
                      )}
                    </rbd.Draggable>
                  ))
                )}
                {provided.placeholder}
              </div>
            )}
          </rbd.Droppable>
        </rbd.DragDropContext>
      </CardCommon>

      {/* Add New Project Modal */}
      <Modal
        visible={isAddProjectModalVisible}
        onClose={handleModalClose}
        caption={formatMessage({ id: 'dbs_add_project' })}
        size="m"
        className="dbs-add-project-modal"
      >
        <Modal.Body>
          <Form>
            <Form.Item label={formatMessage({ id: 'dbs_project' })}>
              <FollowerRange
                className="dbs-follower-range"
                overlayClassName="dbs-follower-range"
                value={selectedProject}
                onChange={(value) => {
                  setSelectedProject(value);
                  console.log('Selected Project: ', value);
                }}
                placeholder={loading ? 'Loading projects...' : formatMessage({ id: 'dbs_please_select_project' })}
                customizePlaceholder={formatMessage({ id: 'dbs_please_input_project_name' })}
                options={projectOptions}
                searchable
                searchPlaceholder={formatMessage({ id: 'dbs_search_project' })}
                isCustomize={true}
                loading={loading}
                // clearable={true}
                matchButtonWidth
              />
            </Form.Item>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            type="primary"
            onClick={handleNextStep}
            disabled={!selectedProject}
            className="dbs-gradient-btn"
          >
            {formatMessage({ id: 'dbs_next_step' })}
          </Button>
          <Button
            onClick={handleModalClose}
          >
            {formatMessage({ id: 'dbs_cancel' })}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add New Content Modal */}
      <AddNewContentModal
        visible={isAddContentModalVisible}
        onClose={handleContentModalClose}
        onConfirm={handleContentConfirm}
        mode={contentModalMode} // 'new' or 'existing'
        selectedProject={selectedProjectOpt}
        existingProjectData={existingProjectData} // specific fields needed for existing project update
        projects={projects} // for calculating next order
        editingContent={editingContent} // content data for editing
        generateTempId={generateTempId}
      />

      {/* Auto-save recovery modal */}
      <AutoSaveModal
        visible={autoSaveVisible}
        onConfirm={() => {
          handleAutoSaveConfirm();
          setIsEditing(true);
        }}
        onClose={handleAutoSaveCancel}
      />

      {/* RoutePrompt to intercept navigation when there are unsaved changes */}
      <RoutePrompt
        when={hasUnsavedChanges}
        onOk={async () => {
          // Save changes before navigating away
          await handleSave();
          // Navigation will proceed after save completes
        }}
      />
    </div>
  );
};

export default WeeklyProjectHighlight;
